import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { styled } from '../../../../../stitches.config';
import { Popover } from '../../../../containers/Popover';
import { Skeleton } from '../../../../layout/Skeleton/Skeleton';
import { Icon } from '../../../../text/Icon/Icon';
import { Button } from '../../../Button/Button';
import { ButtonGroup } from '../../../Button/ButtonGroup';
import { convertMolfileToMol, convertSmilesToMol, getMolByName } from '../../utils/molecule-util/molecule-util.actions';
import { ReactionPreview } from './components/ReactionPreview';
import { CompoundEditorActions } from '../../hooks/useCompoundEditor';

const Form = styled('form', {
  position: 'relative',
  display: 'flex',
  gap: '$3',
});
const Input = styled('input', {
  'border': 'none',
  'borderRadius': '$normal 0 0 $normal',
  'background': '$slate2',
  'padding': '$3 $5 $3 $7',
  'width': '13rem',
  '&:focus': {
    outline: 'none',
  },
});
const InputIcon = styled(Icon, {
  pointerEvents: 'none',
  position: 'absolute',
  top: '$2',
  paddingTop: '$2',
  left: '0',
});

type SearchTypeName = 'smiles' | 'name' | 'molfile';
type SearchType = {
  name: SearchTypeName;
  label: string;
  placeholder: (isReaction: boolean) => string;
  action: (input: string) => void;
};
const searchTypes: SearchType[] = [
  {
    name: 'smiles',
    label: 'SMILES',
    placeholder: (isReaction: boolean) => (isReaction ? 'O=C(O)C.N>>O=C(N)C' : 'O=C(O)C'),
    action: convertSmilesToMol,
  },
  {
    name: 'name',
    label: 'Name / CAS',
    placeholder: (isReaction: boolean) => (isReaction ? 'pyrrole.benzene>>imatinib' : 'pyrrole'),
    action: getMolByName,
  },
  {
    name: 'molfile',
    label: 'Molfile',
    placeholder: (isReaction: boolean) => 'Paste in the molfile contents',
    action: convertMolfileToMol,
  },
];

type InsertCompoundProps = {
  onPickAtom: CompoundEditorActions['onFragmentAtomMode'];
  onPickBond: CompoundEditorActions['onFragmentBondMode'];
  setMols: CompoundEditorActions['setMols'];
  isReaction?: boolean;
};

export const InsertCompound = ({ onPickAtom, onPickBond, setMols, isReaction = false }: InsertCompoundProps) => {
  const [open, setOpen] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>(searchTypes[0]);
  const [searchInput, setSearchInput] = useState('');

  const searchedCompounds = useMemo(() => {
    const [substrates, conditions, product] = searchInput.trim().split('>');
    return {
      substrates: substrates.split('.'),
      product,
    };
  }, [searchInput]);

  const dispatch = useDispatch();

  const { mols, isFetching, errors } = useSelector((state: any) => {
    const { molsBySmiles, molsByName, molsByMolfiles, processing, error } = state.moleculeUtil;
    return {
      mols: {
        smiles: molsBySmiles,
        name: molsByName,
        molfile: molsByMolfiles,
      },
      errors: {
        smiles: error.smilesToMol,
        name: error.nameToMol,
        molfile: error.molfileToMol,
      },
      isFetching: processing > 0,
    };
  });

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    searchedCompounds.substrates.map(substrate => {
      if (substrate) {
        dispatch(searchType.action(substrate));
      }
    });
    if (searchedCompounds.product) {
      dispatch(searchType.action(searchedCompounds.product));
    }
  };

  const possibleMols = {
    substrates: searchedCompounds.substrates.map(s => mols[searchType.name][s]).filter(Boolean),
    product: searchedCompounds.product && mols[searchType.name][searchedCompounds.product],
  };

  return (
    <Popover
      open={open}
      onOpenChange={open => {
        setOpen(open);
      }}
      trigger={
        <Button
          weight="solid"
          theme="purple"
          text="Import"
          icon="input"
          data-test="insert-molecule-button"
          size="small"
        />
      }
      contentProps={{
        sideOffset: 5,
        side: 'bottom',
        css: {
          width: 'auto',
          backgroundColor: '$slate2',
        },
      }}
    >
      <Form onSubmit={onFormSubmit}>
        <InputIcon icon="search" size="small" />
        <Input
          type="text"
          autoFocus
          name="insertMolecule"
          placeholder={searchType.placeholder(isReaction)}
          value={searchInput}
          onChange={event => {
            setSearchInput(event.target.value);
          }}
        />
        <ButtonGroup direction="row">
          {searchTypes.map(type => (
            <Button
              key={type.name}
              type="button"
              weight="minimal"
              active={searchType.name === type.name}
              text={type.label}
              size="small"
              onClick={() => {
                setSearchType(type);
              }}
              css={{ whiteSpace: 'nowrap' }}
            />
          ))}
        </ButtonGroup>
        <Button type="submit" weight="solid" theme="purple" icon="search" size="small" css={{ fontSize: '$4' }} />
      </Form>

      {isFetching ? (
        <Skeleton css={{ height: '12rem', width: '12rem', margin: '$4 0' }} />
      ) : (
        possibleMols.substrates.length > 0 && (
          <ReactionPreview
            mols={possibleMols}
            onPickAtom={onPickAtom}
            onPickBond={onPickBond}
            replaceReaction={setMols}
            onSelected={() => {
              setOpen(false);
            }}
          />
        )
      )}
    </Popover>
  );
};
